import React, { useState } from 'react';
import { Card, Row, Col, Tag, Avatar, Rate, Button, Modal, Descriptions } from 'antd';
import { motion } from 'framer-motion';
import { 
  UserOutlined, 
  CheckCircleOutlined, 
  ClockCircleOutlined, 
  DollarOutlined,
  TrophyOutlined,
  RocketOutlined,
  SafetyCertificateOutlined,
  GlobalOutlined
} from '@ant-design/icons';

interface CaseStudy {
  id: number;
  company: string;
  industry: string;
  challenge: string;
  solution: string;
  result: string;
  timeImprovement: string;
  costSaving: string;
  rating: number;
  avatar: string;
  testimonial: string;
  details: {
    volume: string;
    route: string;
    period: string;
    specialRequirements: string[];
  };
}

const caseStudies: CaseStudy[] = [
  {
    id: 1,
    company: '华南贸易集团',
    industry: '传统B端发美国',
    challenge: '传统客户价格接受度较高，但订单不稳定，发货间隔时间长',
    solution: '318空派专线稳定舱位，享受重货优惠和分批优惠',
    result: '运输成本降低18%，客户续签率达85%',
    timeImprovement: '7-9天稳定时效',
    costSaving: '享受重货1:250减免优惠',
    rating: 5,
    avatar: 'H',
    testimonial: '318空派的价格优势明显，时效稳定，非常适合我们传统贸易客户的需求。',
    details: {
      volume: '月均800CBM',
      route: '深圳 → LAX/SFO机场',
      period: '合作3年',
      specialRequirements: ['重货优惠', '分批发货', '灵活付款']
    }
  },
  {
    id: 2,
    company: 'FBA Supply Chain',
    industry: '美国FBA海外仓',
    challenge: '发货量大且稳定，每周定期都要补货，对时效要求高',
    solution: '318专线每周3个航班保障，预申报快速清关',
    result: '库存周转率提升40%，FBA断货率降至5%以下',
    timeImprovement: '7-9天到仓时效稳定',
    costSaving: '商业地址免附加费',
    rating: 5,
    avatar: 'F',
    testimonial: '作为FBA代发客户，318空派的稳定航班和快速清关帮我们大大提升了库存管理效率。',
    details: {
      volume: '月均1200CBM',
      route: '香港 → JFK/ORD → FBA仓',
      period: '合作2年',
      specialRequirements: ['定期补货', 'FBA标签', '带电产品渠道']
    }
  },
  {
    id: 3,
    company: '环球物流代理',
    industry: '贸易物流同行',
    challenge: '刚开始接触时需要了解清楚产品，对好价格表就行',
    solution: '提供详细产品介绍和竞争优势对比，及时更新价格',
    result: '合作过程比较简单，做好价格表和及时更新价格',
    timeImprovement: '建立合作关系从咨询到成交仅需3天',
    costSaving: '获得更优惠的代理价格',
    rating: 4,
    avatar: 'L',
    testimonial: '318空派的产品介绍详细，价格透明，与同行合作非常愉快。',
    details: {
      volume: '月均600CBM',
      route: '广州/深圳 → LAX/SFO/JFK',
      period: '合作18个月',
      specialRequirements: ['同行代理价格', '灵活结算', '产品培训']
    }
  }
];

const serviceHighlights = [
  {
    icon: <RocketOutlined />,
    title: '极速时效',
    description: '5-8天稳定时效，直飞航班优先',
    color: '#1890ff'
  },
  {
    icon: <SafetyCertificateOutlined />,
    title: '安全保障',
    description: '全程保险覆盖，专业包装指导',
    color: '#52c41a'
  },
  {
    icon: <GlobalOutlined />,
    title: '全域覆盖',
    description: '美国50州全覆盖，偏远地区可达',
    color: '#722ed1'
  },
  {
    icon: <TrophyOutlined />,
    title: '品质服务',
    description: '98%客户满意度，7x24客服支持',
    color: '#faad14'
  }
];

const CaseStudies: React.FC = () => {
  const [selectedCase, setSelectedCase] = useState<CaseStudy | null>(null);
  const [modalVisible, setModalVisible] = useState(false);

  const handleViewDetails = (caseStudy: CaseStudy) => {
    setSelectedCase(caseStudy);
    setModalVisible(true);
  };

  return (
    <div>
      {/* 服务亮点 */}
      <div style={{ marginBottom: '48px' }}>
        <h2 style={{ textAlign: 'center', marginBottom: '32px', fontSize: '28px', fontWeight: 700 }}>
          为什么选择我们
        </h2>
        <Row gutter={[24, 24]}>
          {serviceHighlights.map((highlight, index) => (
            <Col xs={24} sm={12} md={6} key={index}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Card 
                  bordered={false} 
                  style={{ 
                    textAlign: 'center', 
                    height: '100%',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                  }}
                  hoverable
                >
                  <div 
                    style={{ 
                      fontSize: '48px', 
                      color: highlight.color, 
                      marginBottom: '16px' 
                    }}
                  >
                    {highlight.icon}
                  </div>
                  <h3 style={{ marginBottom: '12px' }}>{highlight.title}</h3>
                  <p style={{ color: '#666', margin: 0 }}>{highlight.description}</p>
                </Card>
              </motion.div>
            </Col>
          ))}
        </Row>
      </div>

      {/* 客户案例 */}
      <div>
        <h2 style={{ textAlign: 'center', marginBottom: '32px', fontSize: '28px', fontWeight: 700 }}>
          成功案例
        </h2>
        <Row gutter={[24, 24]}>
          {caseStudies.map((caseStudy, index) => (
            <Col xs={24} lg={8} key={caseStudy.id}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
              >
                <Card
                  hoverable
                  style={{ 
                    height: '100%',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                  }}
                  actions={[
                    <Button type="link" onClick={() => handleViewDetails(caseStudy)}>
                      查看详情
                    </Button>
                  ]}
                >
                  <Card.Meta
                    avatar={
                      <Avatar 
                        size={48} 
                        style={{ backgroundColor: '#1890ff' }}
                        icon={<UserOutlined />}
                      >
                        {caseStudy.avatar}
                      </Avatar>
                    }
                    title={
                      <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span>{caseStudy.company}</span>
                          <Tag color="blue">{caseStudy.industry}</Tag>
                        </div>
                        <Rate disabled defaultValue={caseStudy.rating} style={{ marginTop: '8px', fontSize: '14px' }} />
                      </div>
                    }
                    description={
                      <div style={{ marginTop: '12px' }}>
                        <p style={{ margin: '8px 0', fontSize: '13px', color: '#666' }}>
                          <strong>挑战：</strong>{caseStudy.challenge}
                        </p>
                        <div style={{ margin: '12px 0' }}>
                          <Tag color="green" icon={<ClockCircleOutlined />}>
                            {caseStudy.timeImprovement}
                          </Tag>
                          <Tag color="orange" icon={<DollarOutlined />}>
                            {caseStudy.costSaving}
                          </Tag>
                        </div>
                        <p style={{ 
                          fontStyle: 'italic', 
                          color: '#666', 
                          fontSize: '12px',
                          borderLeft: '3px solid #1890ff',
                          paddingLeft: '12px',
                          margin: '12px 0 0 0'
                        }}>
                          "{caseStudy.testimonial}"
                        </p>
                      </div>
                    }
                  />
                </Card>
              </motion.div>
            </Col>
          ))}
        </Row>
      </div>

      {/* 案例详情弹窗 */}
      <Modal
        title={selectedCase?.company + ' - 详细案例'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={700}
      >
        {selectedCase && (
          <div>
            <Descriptions bordered column={2} style={{ marginBottom: '24px' }}>
              <Descriptions.Item label="行业类型">{selectedCase.industry}</Descriptions.Item>
              <Descriptions.Item label="合作时长">{selectedCase.details.period}</Descriptions.Item>
              <Descriptions.Item label="月均货量">{selectedCase.details.volume}</Descriptions.Item>
              <Descriptions.Item label="主要路线">{selectedCase.details.route}</Descriptions.Item>
            </Descriptions>
            
            <div style={{ marginBottom: '16px' }}>
              <h4>特殊要求：</h4>
              {selectedCase.details.specialRequirements.map((req, index) => (
                <Tag key={index} style={{ margin: '4px 4px 4px 0' }}>{req}</Tag>
              ))}
            </div>

            <div style={{ marginBottom: '16px' }}>
              <h4>解决方案：</h4>
              <p>{selectedCase.solution}</p>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <h4>取得成果：</h4>
              <p>{selectedCase.result}</p>
            </div>

            <div style={{ 
              background: '#f6ffed', 
              border: '1px solid #b7eb8f', 
              borderRadius: '6px', 
              padding: '16px' 
            }}>
              <CheckCircleOutlined style={{ color: '#52c41a', marginRight: '8px' }} />
              <strong>客户评价：</strong>"{selectedCase.testimonial}"
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default CaseStudies;
